import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FormAutosuggest, useWatch } from '@fs/zion-form'
import { ContentAdd, ContentDelete } from '@fs/zion-icon'
import { Cell, Grid, IconButton, LabelSpacer, ListItem, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useSlsStateContext } from '@fs/zion-sls'
import { getGroupedGenderedRelTypeSuggestions, parseRelKey } from '../utils/familyMember.utils'
import FamilyMemberNameFormOverlay from './FamilyMemberNameFormOverlay'
import RemoveHouseholdMemberDialogOverlay from './RemoveHouseholdMemberDialogOverlay'

const RelationshipInputs = ({
  refs,
  field,
  index,
  namesAutoSuggestions,
  formMethods,
  fieldArrayMethods,
  dialogMode = false,
  onRelationshipChange = () => {},
  onNameChange = () => {},
}) => {
  const [t] = useTranslation()
  const { types } = useSlsStateContext()
  const namesOverlay = useOverlay()
  const removeOverlay = useOverlay()
  const { update, remove } = fieldArrayMethods

  const [relationship, targetRecordId] = useMemo(() => {
    return formMethods.getValues([`relationships[${index}]`, 'targetRecordId'])
  }, [formMethods, index])

  const handleRelationshipChange = useCallback(
    (suggestion) => {
      const { relKey, order } = parseRelKey(suggestion?.key)

      // Update derived form fields only
      formMethods.setValue(`relationships[${index}].relationshipType`, relKey, { shouldDirty: true })
      formMethods.setValue(`relationships[${index}].relationshipDirection`, order, { shouldDirty: true })
      // FormAutosuggest already sets `relationships[${index}].relationship` via the name prop

      // Inform parent about the change
      onRelationshipChange(suggestion, field, index)
    },
    [formMethods, index, field, onRelationshipChange]
  )

  const handleNameChange = useCallback(
    (suggestion) => {
      // Handle clear/empty selection
      if (!suggestion?.key) {
        formMethods.setValue(`relationships[${index}].personId`, null, { shouldDirty: true })
        // FormAutosuggest already clears `relationships[${index}].value` via the name prop

        // Inform parent about the name being cleared
        onNameChange(null, field, index)
        return
      }

      // Update derived personId field only
      formMethods.setValue(`relationships[${index}].personId`, suggestion.key, { shouldDirty: true })
      // FormAutosuggest already sets `relationships[${index}].value` via the name prop
      formMethods.setValue(`relationships[${index}].recordId`, suggestion.recordId || null, { shouldDirty: true })

      // Trigger validation for the whole relationship group
      formMethods.trigger('relationships')

      // Inform parent about the name change
      onNameChange(suggestion, field, index)
    },
    [formMethods, index, field, onNameChange]
  )

  const handleDelete = useCallback(() => {
    if ((relationship?.slsRelationshipId || relationship?.recordId === targetRecordId) && !relationship?.isNew) {
      // Existing relationship - open remove dialog to confirm
      removeOverlay.open()
    } else {
      // New relationship - remove from array entirely
      remove(index)
    }
    formMethods.trigger('relationships')
  }, [relationship, removeOverlay, remove, index, targetRecordId, formMethods])

  const handleSaveOnRemoveDialog = useCallback(() => {
    // Existing relationship - mark as deleted
    update(index, { ...relationship, isDeleted: true })
  }, [index, relationship, update])

  // Determine if the Name field should be disabled:
  // - when in dialog mode
  // - or when the relationship is linked to an existing SLS relationship
  // - or when the relationship's recordId matches the targetRecordId
  // and the relationship is not new
  const disableNameField = useMemo(() => {
    return (
      (dialogMode || !!relationship?.slsRelationshipId || relationship?.recordId === targetRecordId) &&
      !relationship.isNew
    )
  }, [dialogMode, relationship, targetRecordId])

  const selectedPersonId = useWatch({ name: `relationships[${index}].personId`, control: formMethods.control })

  const relationshipSuggestions = useMemo(() => {
    return getGroupedGenderedRelTypeSuggestions(types, undefined, true)
  }, [types])

  // If the field is marked as deleted, do not render it, we handle this becaause we need to keep the indexes consistent
  if (field.isDeleted) {
    return null
  }

  return (
    <Grid key={field.id} gutters="nano" alignY="middle">
      <FormAutosuggest
        ref={
          dialogMode
            ? undefined
            : (el) => {
                refs.current[index] = el
              }
        }
        columns={dialogMode ? '8' : '6'}
        data-testid={`familyMemberNameField-${index}`}
        label="Name"
        suggestions={namesAutoSuggestions}
        name={`relationships[${index}].value`}
        onSuggestionSelected={handleNameChange}
        disabled={disableNameField}
        lastResort={{
          key: 'LAST-RESORT-KEY',
          primaryText: t('household-details-card.add-person', 'Add Person'),
          startElement: <ListItem.Icon Icon={ContentAdd} />,
          onClick: () => {
            namesOverlay.open({ target: refs.current[index] })
          },
        }}
        clearable
        dense
      />

      <FormAutosuggest
        columns={dialogMode ? '4' : '5'}
        data-testid={`familyMemberRelationshipField-${index}`}
        suggestions={relationshipSuggestions}
        label="Relationship"
        name={`relationships[${index}].relationship`}
        onSuggestionSelected={handleRelationshipChange}
        clearable
        dense
      />

      {!dialogMode && (
        <Cell columns="1">
          <LabelSpacer />
          <IconButton
            Icon={ContentDelete}
            color="red"
            onClick={handleDelete}
            data-testid={`household-member-delete-button-${index}`}
            title={t('household-details-card.remove-from-household-button', 'Remove from household')}
          />

          <FamilyMemberNameFormOverlay
            overlay={namesOverlay}
            index={index}
            basePath={`relationships[${index}].newPersonCapture`}
            overlayProps={{
              placement: 'bottom-end',
            }}
          />

          <RemoveHouseholdMemberDialogOverlay
            overlay={removeOverlay}
            personId={selectedPersonId}
            onSave={handleSaveOnRemoveDialog}
          />
        </Cell>
      )}
    </Grid>
  )
}

RelationshipInputs.propTypes = {
  refs: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    current: PropTypes.array.isRequired,
  }).isRequired,
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isDeleted: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  namesAutoSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      recordId: PropTypes.string,
      primaryText: PropTypes.string,
    })
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  formMethods: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  fieldArrayMethods: PropTypes.object.isRequired,
  dialogMode: PropTypes.bool,
  onRelationshipChange: PropTypes.func,
  onNameChange: PropTypes.func,
}

export default RelationshipInputs
